import * as React from 'react';
import { useInterval } from 'react-use';
import { Options as LoadDataOptions, useLoadData } from './useLoadData';

/**
 * Custom hook to poll data.
 *
 * To prevent calls from piling up when response time > interval,
 * subsequent poll requests are ignored until the pending request resolves.
 */
export function useLoadDataPeriodically<T>(
  loadData: (prevData: T | null) => Promise<T>,
  /** poll interval in milliseconds; null to disable */
  intervalMs: number | null = 1000,
  options?: LoadDataOptions,
) {
  const [tick, setTick] = React.useState(intervalMs); // null prevents initial load

  const loaded = useLoadData<T | null>(loadData, [tick], {
    skip: tick === null,
    ...options,
  });

  const triggerLoad = () => setTick(Date.now());

  useInterval(() => {
    if (loaded.isLoading) {
      // skip poll attempt due to pending request
      return;
    }

    triggerLoad();
  }, intervalMs);

  return {
    ...loaded,
    triggerLoad,
  };
}
